/**
 * ZAXIA Staff Auth Context
 * Centralized authentication state management for the staff portal
 *
 * Phase P2.1-3: Global Auth Context & Session Management
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import type { StaffUser } from '../api/authApi';
import {
  login as apiLogin,
  getCurrentUser,
  getToken,
  setUser as storeUser,
  clearAuth,
  getUser as getStoredUser,
} from '../api/authApi';

// =============================================================================
// AUTH CONTEXT TYPES
// =============================================================================

export interface AuthState {
  user: StaffUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshSession: () => Promise<void>;
}

// =============================================================================
// AUTH CONTEXT
// =============================================================================

export const AuthContext = createContext<AuthContextValue | null>(null);

// =============================================================================
// AUTH PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Auth state
  const [user, setUser] = useState<StaffUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Derived state
  const isAuthenticated = token !== null && user !== null;

  // ==========================================================================
  // LOGOUT
  // ==========================================================================

  const logout = useCallback(() => {
    console.log('[AUTH] Logging out...');
    // Clear context state
    setUser(null);
    setTokenState(null);
    // Clear storage
    clearAuth();
  }, []);

  // ==========================================================================
  // SESSION REFRESH / VALIDATION
  // ==========================================================================

  const refreshSession = useCallback(async () => {
    console.log('[AUTH] Refreshing session...');

    const storedToken = getToken();
    if (!storedToken) {
      console.log('[AUTH] No token found, clearing state');
      setUser(null);
      setTokenState(null);
      setIsLoading(false);
      return;
    }

    try {
      // Validate token by calling /api/auth/me
      const currentUser = await getCurrentUser();
      console.log('[AUTH] Session valid, user:', currentUser.email);

      // Update context state
      setUser(currentUser);
      setTokenState(storedToken);
      // Update stored user (in case it changed)
      storeUser(currentUser);
    } catch (error) {
      console.log('[AUTH] Session invalid, forcing logout');

      // Check if it's a 401 error (handled in getCurrentUser)
      // Clear everything regardless
      setUser(null);
      setTokenState(null);
      clearAuth();

      // The error is thrown for 401 in getCurrentUser which also clears auth
      // So this is just ensuring state is clean
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ==========================================================================
  // LOGIN
  // ==========================================================================

  const login = useCallback(async (email: string, password: string) => {
    console.log('[AUTH] Attempting login for:', email);

    // Call API login (handles token storage internally)
    const result = await apiLogin({ email, password });

    console.log('[AUTH] Login successful, updating context');

    // Update context state
    setUser(result.user);
    setTokenState(result.token);
  }, []);

  // ==========================================================================
  // INITIAL SESSION CHECK
  // ==========================================================================

  useEffect(() => {
    console.log('[AUTH] Initializing auth context...');

    // First, try to load user from storage immediately (for faster UI)
    const storedUser = getStoredUser();
    const storedToken = getToken();

    if (storedUser && storedToken) {
      // Temporarily set state while we validate
      setUser(storedUser);
      setTokenState(storedToken);
    }

    // Then validate the session
    refreshSession();
  }, [refreshSession]);

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated,
      isLoading,
      login,
      logout,
      refreshSession,
    }),
    [user, token, isAuthenticated, isLoading, login, logout, refreshSession]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
